/* eslint-disable no-unused-vars */
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AdminContext } from "../../../context/AdminContext";
import { useTheme } from "../../../context/ThemeContext";
import {
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader,
} from "lucide-react";
import { toast } from "react-toastify";

const CategoryList = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const {
    aToken,
    serviceCategories,
    fetchServiceCategories,
    toggleServiceCategoryActive,
    deleteServiceCategory,
  } = useContext(AdminContext);

  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    categoryId: null,
  });

  useEffect(() => {
    if (aToken) {
      fetchServiceCategories();
    }
  }, [aToken, fetchServiceCategories]);

  const handleToggleActive = async (categoryId, currentStatus) => {
    const data = await toggleServiceCategoryActive(categoryId);
    if (!data?.success) {
      toast.error("Failed to toggle category");
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const data = await deleteServiceCategory(deleteModal.categoryId);
      if (data.success) {
        setDeleteModal({ show: false, categoryId: null });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1
            className={`text-2xl font-bold ${
              isDarkMode ? "text-dark-text" : "text-text"
            }`}
          >
            Service Categories
          </h1>
          <p
            className={`text-sm mt-1 ${
              isDarkMode ? "text-dark-muted" : "text-gray-600"
            }`}
          >
            Manage dynamic service categories and their fields
          </p>
        </div>
        <button
          onClick={() => navigate("/categories/create")}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus size={20} />
          Create Category
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(serviceCategories || []).map((category) => (
          <div
            key={category.id}
            className={`p-5 rounded-xl border transition-all ${
              isDarkMode
                ? "bg-dark-surface border-dark-border hover:border-primary/50"
                : "bg-card border-accent hover:border-primary/50"
            }`}
          >
            {/* Category Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {category.iconImage ? (
                  <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                    <img
                      src={category.iconImage}
                      alt={category.label}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl border border-gray-200 dark:border-gray-600"
                    style={{
                      backgroundColor: `${category.color}20`,
                      color: category.color,
                    }}
                  >
                    📋
                  </div>
                )}
                <div>
                  <h3
                    className={`font-semibold ${
                      isDarkMode ? "text-dark-text" : "text-text"
                    }`}
                  >
                    {category.label}
                  </h3>
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-dark-muted" : "text-gray-500"
                    }`}
                  >
                    {category.name}
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  handleToggleActive(category.id, category.isActive)
                }
                className="p-1"
              >
                {category.isActive ? (
                  <ToggleRight size={24} className="text-green-500" />
                ) : (
                  <ToggleLeft size={24} className="text-gray-400" />
                )}
              </button>
            </div>

            {/* Category Stats */}
            <div className="flex gap-4 mb-4">
              <div>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-dark-muted" : "text-gray-500"
                  }`}
                >
                  Fields
                </p>
                <p
                  className={`text-lg font-semibold ${
                    isDarkMode ? "text-dark-text" : "text-text"
                  }`}
                >
                  {category.fields?.length || 0}
                </p>
              </div>
              <div>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-dark-muted" : "text-gray-500"
                  }`}
                >
                  Caregivers
                </p>
                <p
                  className={`text-lg font-semibold ${
                    isDarkMode ? "text-dark-text" : "text-text"
                  }`}
                >
                  {category._count?.caregivers || 0}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/categories/${category.id}/edit`)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-dark-background text-dark-text hover:bg-dark-border"
                    : "bg-accent text-text hover:bg-gray-300"
                }`}
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                onClick={() =>
                  setDeleteModal({ show: true, categoryId: category.id })
                }
                className="px-3 py-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {(!serviceCategories || serviceCategories.length === 0) && (
        <div
          className={`text-center py-12 rounded-xl border-2 border-dashed ${
            isDarkMode ? "border-dark-border" : "border-accent"
          }`}
        >
          <p
            className={`text-lg mb-2 ${
              isDarkMode ? "text-dark-muted" : "text-gray-500"
            }`}
          >
            No categories yet
          </p>
          <button
            onClick={() => navigate("/categories/create")}
            className="text-primary hover:underline"
          >
            Create your first category
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            className={`p-6 rounded-xl max-w-md w-full mx-4 ${
              isDarkMode ? "bg-dark-surface" : "bg-card"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDarkMode ? "text-dark-text" : "text-text"
              }`}
            >
              Delete Category
            </h3>
            <p
              className={`mb-6 ${
                isDarkMode ? "text-dark-muted" : "text-gray-600"
              }`}
            >
              Are you sure you want to delete this category? This action cannot
              be undone. All associated fields will also be deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  setDeleteModal({ show: false, categoryId: null })
                }
                disabled={loading}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-dark-background text-dark-text hover:bg-dark-border"
                    : "bg-accent text-text hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader size={16} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;
